import { Controller } from "@hotwired/stimulus";

// GO4S Design System — Sidebar (inspirée de GO4SPILOT).
// Gère : drawer mobile (open/close + overlay + scroll-lock + Échap) et repli
// desktop (collapse/expand) persisté dans localStorage.
// Spec : docs/design-system/REFERENCE.md §4 + docs/design-system/RESPONSIVE.md.
const STORAGE_KEY = "ds-sidebar-collapsed";
const DESKTOP_BP = 1024; // ≥ : sidebar statique déployée + repli persisté ; <1024 = tiroir

export default class extends Controller {
  static targets = ["sidebar", "overlay"];

  connect() {
    if (window.innerWidth >= DESKTOP_BP && localStorage.getItem(STORAGE_KEY) === "1") {
      this.sidebarTarget.classList.add("is-collapsed");
    }
    this._onKeydown = (e) => { if (e.key === "Escape") this.close(); };
    this._onResize = () => { if (window.innerWidth >= DESKTOP_BP) this.close(); };
    window.addEventListener("resize", this._onResize);

    // Conserve la position de défilement du menu à travers les navigations
    // Turbo (sinon la sidebar remonte en haut à chaque clic et le menu actif
    // n'est plus visible sans re-scroller).
    this._nav = this.element.querySelector(".ds-sidebar__nav");
    if (this._nav) {
      this._restoreNavScroll();
      this._onNavScroll = () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = null;
          this._saveNavScroll();
        });
      };
      this._nav.addEventListener("scroll", this._onNavScroll, { passive: true });
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize);
    document.removeEventListener("keydown", this._onKeydown);
    document.body.classList.remove("ds-scroll-lock");
    if (this._nav && this._onNavScroll) {
      this._nav.removeEventListener("scroll", this._onNavScroll);
    }
  }

  // --- Persistance du défilement du menu (par espace) ---
  _navScrollKey() {
    // Clé par espace (1er segment après la locale) pour ne pas mélanger
    // les positions entre admin / parents / écoles…
    const seg = window.location.pathname.split("/").filter(Boolean);
    return `ds-sidebar-scroll:${seg[1] || seg[0] || "root"}`;
  }

  _saveNavScroll() {
    if (!this._nav) return;
    try { sessionStorage.setItem(this._navScrollKey(), String(this._nav.scrollTop)); } catch (_) { /* quota/privé */ }
  }

  _restoreNavScroll() {
    if (!this._nav) return;
    let value;
    try { value = sessionStorage.getItem(this._navScrollKey()); } catch (_) { value = null; }
    if (value != null) this._nav.scrollTop = parseInt(value, 10) || 0;
  }

  // --- Drawer mobile ---
  toggle() {
    if (this.sidebarTarget.classList.contains("is-open")) this.close();
    else this.open();
  }

  open() {
    this.sidebarTarget.classList.add("is-open");
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("is-open");
    document.body.classList.add("ds-scroll-lock");
    document.addEventListener("keydown", this._onKeydown);
  }

  close() {
    this.sidebarTarget.classList.remove("is-open");
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("is-open");
    document.body.classList.remove("ds-scroll-lock");
    document.removeEventListener("keydown", this._onKeydown);
  }

  // --- Repli desktop (persisté) ---
  collapse() {
    this.sidebarTarget.classList.add("is-collapsed");
    localStorage.setItem(STORAGE_KEY, "1");
  }

  expand() {
    this.sidebarTarget.classList.remove("is-collapsed");
    localStorage.setItem(STORAGE_KEY, "0");
  }

  toggleCollapse() {
    if (this.sidebarTarget.classList.contains("is-collapsed")) {
      this.expand();
    } else {
      this.collapse();
    }
  }
}
